import { useEffect, useMemo, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation, Trans } from 'next-i18next';
import { useRouter } from 'next/router';
import Link from 'next/link';
import {
  ArrowRightIcon,
  BellAlertIcon,
  BriefcaseIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@restworld/utils-common';

import { imageVariant, toDateFormat, useAnalytics } from '@restworld/utils-common';
import { JobPosition } from '@restworld/utility-types';
import { Button, Heading, IconButton, Modal, ModalContent, Spinner } from '@restworld/ui-ds-base';

import { offlineJpModalOpenAtom } from './offline-jp.atoms';
import { SimilarJobsList } from './similar-jobs-list';
import { useSimilarJobPositionsPaginated } from '../../hooks/use-similar-job-positions-paginated';
import { hasUserAppliedSpontaneousApplicationAtom } from '../worker-entry-point/use-show-spontaneous-application';
import { entryPointModalVisibleAtom } from '../worker-entry-point/worker-entry-point-modal';
import { saveResearchModalV2Atom } from '../job-explorer/job-explorer-restaurant-explorer.atoms';
import { areaSelectionAtom } from '../job-explorer/job-explorer-map/job-explorer-save-research-v2/job-explorer-save-research-v2';
import { ExternalResourceCard } from '../external-resource-card';

/**
 * Salary hub on the marketing site (Osservatorio): median net pay per role,
 * percentiles, geography, career progression. Verified final URL: responds 200
 * with no redirect.
 *
 * Deliberately the hub and not `/stipendi/<role>`: per-role pages exist for
 * four roles only (cameriere, cuoco, barista, pizzaiolo) and mapping a JP's
 * occupation onto them needs an id table we do not have yet. The hub is
 * relevant for every role, so it ships now; per-role is a later upgrade.
 */
const SALARY_GUIDE_URL = 'https://www.restworld.it/stipendi';

// Dynamic import (no SSR) — same pattern as `JobApplicationEntryPoint`.
// Avoids pulling the entry-point flow + its atoms during server render.
const WorkerEntryPointButton = dynamic(
  () => import('../worker-entry-point').then((mod) => mod.WorkerEntryPointButton),
  { ssr: false }
);

type Scenario = 'A' | 'A_hybrid_thin' | 'B';

interface OfflineJobLandingModalProps {
  jobPositionApiData: Partial<JobPosition>;
}

// Long-scroll, marketing-oriented modal for offline JPs. Layout:
//
//   - Sticky 1-line header: just the title.
//   - Scrollable body: alternating content sections + CTAs:
//       1. Intro citing the offline JP's role
//       2. (A / A_hybrid_thin only) "Ecco offerte simili a [role]" + paginated list
//       3. Soft transition + spontaneous-app card (only if `restaurant.show_details`)
//       4. Save-search card — opens the JobExplorerSaveResearchV2 wizard
//       5. Explore link (final fallback)
//
// CTAs are interleaved with explanatory copy so each click is well-framed
// (no naked button stack). No footer — exits live in the body.
//
// Loading: body shows a skeleton until the paginated similar-JPs hook
// resolves. Scenario gating then runs against the resolved item count.
export const OfflineJobLandingModal = ({ jobPositionApiData }: OfflineJobLandingModalProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [open, setOpen] = useAtom(offlineJpModalOpenAtom);
  const didUserSpontaneousApply = useAtomValue(hasUserAppliedSpontaneousApplicationAtom);

  const close = useCallback(() => setOpen(false), [setOpen]);

  // Auto-open on mount. The trigger button below "Candidati" re-opens after dismiss.
  useEffect(() => {
    setOpen(true);
  }, [setOpen]);

  // When the user clicks "Send spontaneous application" we delegate to
  // `WorkerEntryPointButton`, which opens its own entry-point modal. To
  // avoid stacking two modals on top of each other, dismiss this one as
  // soon as the entry-point modal becomes visible.
  const entryPointVisible = useAtomValue(entryPointModalVisibleAtom);
  useEffect(() => {
    if (entryPointVisible && open) close();
  }, [entryPointVisible, open, close]);

  const restaurant = jobPositionApiData?.restaurant;
  const showRestaurantDetails = !!restaurant?.show_details;
  const jobPositionId = jobPositionApiData?.id;
  const showSpontaneous = showRestaurantDetails && !didUserSpontaneousApply;

  // Origin role label, used in copy ("Ecco offerte simili a Aiuto cuoco").
  // Falls back to occupation label, then to a generic word.
  const originRole =
    jobPositionApiData?.job_title ||
    jobPositionApiData?.occupation?.label_it?.label ||
    t('job-position-page:offline_modal_role_fallback');

  const restaurantName = restaurant?.restaurant_name;
  const restaurantCity = restaurant?.locality;
  // Prefer the explicit logo; fall back to the lowest-`order` entry in the
  // restaurant gallery so we still show *something* recognizable when the
  // employer hasn't uploaded a square logo. If neither exists we render no
  // image at all (no generic icon).
  const firstPictureUrl = (restaurant?.pictures ?? [])
    .slice()
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))[0]?.url;
  // Modal shows ~120-180px restaurant image → `_sm` is enough.
  const restaurantImage =
    imageVariant(restaurant?.restaurant_logo, 'sm') || imageVariant(firstPictureUrl, 'sm') || null;
  // Date + time are formatted with date-fns in the browser's local timezone
  // (date-fns `format` reads the system tz). Italian is used as the locale
  // for month names so "15 aprile 2026" doesn't read as "15 April 2026"
  // even on EN routes — matches the rest of the app.
  const wentOfflineAt = jobPositionApiData?.went_offline_at;
  const wentOfflineDate = wentOfflineAt ? toDateFormat(wentOfflineAt, 'd MMMM yyyy') : null;
  const wentOfflineTime = wentOfflineAt ? toDateFormat(wentOfflineAt, 'HH:mm') : null;

  // Modal owns the paginated fetch so we can wait for the first page and
  // pick the scenario based on the actual count, instead of trusting the
  // legacy atom (which can be stale or empty if the page-level effect
  // hasn't fired yet).
  const {
    items: similarItems,
    hasMore,
    isLoading,
    isInitialLoading,
    loadMore,
  } = useSimilarJobPositionsPaginated(jobPositionId);

  // Dev-only override to preview the 3 variants without needing 3 different JPs.
  const router = useRouter();
  const variantOverride =
    process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production'
      ? (router?.query?.['offline_variant'] as Scenario | undefined)
      : undefined;

  const scenario: Scenario | null = useMemo(() => {
    if (variantOverride === 'A' || variantOverride === 'A_hybrid_thin' || variantOverride === 'B') {
      return variantOverride;
    }
    // Wait for the first page to resolve before locking the scenario —
    // otherwise we'd flash "no similar jobs" for everyone during fetch.
    if (isInitialLoading) return null;
    const n = similarItems?.length ?? 0;
    if (n >= 3) return 'A';
    if (n >= 1) return 'A_hybrid_thin';
    return 'B';
  }, [variantOverride, isInitialLoading, similarItems?.length]);

  const onClickExplore = useCallback(() => {
    trackEvent('worker_clicked_explore_from_offline', {
      job_position_id: jobPositionId,
      scenario,
      destination: showRestaurantDetails ? 'restaurant_page' : 'job_explorer',
    });
  }, [jobPositionId, scenario, showRestaurantDetails, trackEvent]);

  const onClickSpontaneous = useCallback(() => {
    trackEvent('worker_started_spontaneous_app_from_offline', {
      job_position_id: jobPositionId,
      scenario,
    });
  }, [jobPositionId, scenario, trackEvent]);

  // Save-search CTA: open the existing JobExplorerSaveResearchV2 wizard
  // (already mounted on the JP page with prefill) instead of capturing
  // email inline. The wizard lets the user review categories, area, and
  // notification frequency — important because the right defaults depend
  // on `advertised_location` (LOCAL → 20km radius; NATIONAL → whole Italy)
  // and we shouldn't lock the user into our assumptions.
  const setSaveResearchModalOpen = useSetAtom(saveResearchModalV2Atom);
  const setAreaSelection = useSetAtom(areaSelectionAtom);
  const onClickSaveSearch = useCallback(() => {
    trackEvent('worker_subscribed_to_similar_jobs_alert', {
      job_position_id: jobPositionId,
      scenario,
      via: 'wizard',
    });
    // Pre-pick the area mode based on the origin JP's advertised_location.
    // The user can still flip it inside the wizard's step 2.
    const advertised = jobPositionApiData?.advertised_location;
    setAreaSelection(advertised === 'national' ? 'whole_italy' : 'regional');
    setSaveResearchModalOpen(true);
    close();
  }, [
    close,
    jobPositionApiData?.advertised_location,
    jobPositionId,
    scenario,
    setAreaSelection,
    setSaveResearchModalOpen,
    trackEvent,
  ]);

  if (!jobPositionId) return null;

  return (
    <Modal open={open} onOpenChange={(v) => !v && close()}>
      {/* Width: 90vw on tablet+, capped at 64rem on huge screens.
          Height: explicit h-[85dvh] so the inner body's overflow-y-auto engages.
          noPadding: we manage all padding inside CustomHeader / Body ourselves. */}
      <ModalContent
        className="w-full md:w-[90vw] md:max-w-[56rem] lg:max-w-[64rem] !min-h-0 h-[85dvh]"
        hideHeader
        noPadding
      >
        {/* Custom sticky header on a single row: title on the left, close button on
            the right. Hidden on mobile — the Drawer's swipe-to-dismiss gesture is
            the dismiss affordance there, and the Hero section already restates the
            "non accetta più candidature" message; an extra header eats vertical
            space we can't afford on small viewports. */}
        <div className="hidden md:flex sticky top-0 z-10 bg-white rounded-t-2xl flex-row items-center justify-between gap-3 w-full px-6 py-3 border-b border-gray-100">
          {/* On mobile the title wraps to 2 lines instead of truncating mid-word. */}
          <h3 className="flex-1 min-w-0 text-base md:text-lg font-semibold text-gray-900 leading-snug">
            {t('job-position-page:offline_modal_title')}
          </h3>
          <IconButton ariaLabel={t('common:close')} Icon={XMarkIcon} onClick={close} />
        </div>

        {/* Body: generous gap between sections so the modal reads as a calm,
            product-marketing-ish flow. Vertical padding on top breathes after
            the sticky header border. */}
        <div className="flex flex-col gap-10 md:gap-12 w-full px-3 md:px-8 pb-8 pt-6 md:pt-8 flex-1 overflow-y-auto">
          {scenario === null ? (
            <div className="flex justify-center items-center min-h-[40vh]">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              {/* Emotional opener: the "sad" beat names what happened (with the */}
              {/* exact date if we have it), the "hopeful" beat reframes toward  */}
              {/* the alternatives below. Small but visually distinct so the     */}
              {/* user reads it before scrolling into the cards.                 */}
              {/* Two-beat opener: muted "sad" line gets a left accent border to feel like
                  a quote/note; "hopeful" line is plain body color. Minimal so the visual
                  stays calm — the contrast between the two beats does the work. */}
              <div className="flex flex-col gap-2 w-full pl-4 border-l-4 border-purple-300">
                <p className="text-base md:text-lg font-semibold text-gray-900 leading-snug">
                  {wentOfflineDate ? (
                    <Trans
                      i18nKey="job-position-page:offline_modal_hero_sad_with_date"
                      values={{ role: originRole, date: wentOfflineDate, time: wentOfflineTime }}
                      components={{ strong: <strong /> }}
                    />
                  ) : (
                    <Trans
                      i18nKey="job-position-page:offline_modal_hero_sad"
                      values={{ role: originRole }}
                      components={{ strong: <strong /> }}
                    />
                  )}
                </p>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {t('job-position-page:offline_modal_hero_hopeful')}
                </p>
              </div>

              {scenario !== 'B' ? (
                <section className="flex flex-col gap-3 w-full">
                  {/* Title icon: BriefcasePurple800Icon = BriefcaseIcon with text-primary-800 */}
                  <div className="flex items-center gap-2">
                    <BriefcaseIcon className="h-6 w-6 text-primary-800 shrink-0" />
                    <Heading size={4} weight="semibold" color="default">
                      <Trans
                        i18nKey="job-position-page:offline_modal_similar_section_title"
                        values={{ role: originRole }}
                        components={{ strong: <strong /> }}
                      />
                    </Heading>
                  </div>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                    {t('job-position-page:offline_modal_similar_section_lead')}
                  </p>
                  <SimilarJobsList
                    items={similarItems}
                    hasMore={hasMore}
                    isLoading={isLoading}
                    onLoadMore={loadMore}
                    onItemClick={(item, idx) =>
                      trackEvent('worker_clicked_similar_job_from_offline', {
                        job_position_id: jobPositionId,
                        clicked_jp_id: item.id,
                        position_in_list: idx,
                        scenario,
                      })
                    }
                  />
                </section>
              ) : (
                <section className="flex flex-col gap-3 w-full">
                  {/* Title icon: ClockPurpleIcon = ClockIcon with text-primary-800 */}
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-primary-800 shrink-0" />
                    <Heading size={4} weight="semibold" color="default">
                      {t('job-position-page:offline_modal_no_similar_title')}
                    </Heading>
                  </div>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                    {t('job-position-page:offline_modal_no_similar_lead')}
                  </p>
                </section>
              )}

              {showSpontaneous ? (
                // CTA card: filled tinted = primary placement (scenario B);
                // neutral white = secondary placement (A / A_hybrid_thin).
                <div
                  className={cn(
                    'flex flex-col gap-4 p-5 md:p-6 rounded-2xl border',
                    scenario === 'B' ? 'bg-purple-50 border-purple-100' : 'bg-white border-gray-200'
                  )}
                >
                  {/* Spontaneous card header: restaurant logo sits next to the title
                      + city subtitle so the user immediately recognizes WHERE they'd
                      be sending the spontaneous application. */}
                  <div className="flex flex-row items-center gap-3 w-full">
                    {restaurantImage ? (
                      <img
                        src={restaurantImage}
                        alt={restaurantName ?? ''}
                        className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover bg-white border border-gray-200 shrink-0"
                      />
                    ) : null}
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                      <Heading size={4} weight="semibold" color="default">
                        <Trans
                          i18nKey="job-position-page:offline_modal_spontaneous_title"
                          values={{ restaurantName: restaurantName ?? '' }}
                          components={{ strong: <strong /> }}
                        />
                      </Heading>
                      {(restaurantName || restaurantCity) && (
                        <p className="text-xs md:text-sm text-gray-600 truncate">
                          {restaurantName}
                          {restaurantName && restaurantCity ? ' · ' : ''}
                          {restaurantCity}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                    {t('job-position-page:offline_modal_spontaneous_lead')}
                  </p>
                  {/* Reuses the same entry-point flow as the spontaneous CTA    */}
                  {/* on the restaurant detail page. Children pattern: the       */}
                  {/* button's `cloneElement` injects its `onClick=handleClick`  */}
                  {/* into our styled Button, so we keep our scenario-driven     */}
                  {/* variant styling. The useEffect above closes this modal     */}
                  {/* when the entry-point modal becomes visible.                */}
                  {/* Wraps entry-point button so we can fire tracking without
                      fighting the cloneElement-injected onClick. Click bubbles
                      up through this div before the button's own onClick. */}
                  <div className="w-full" onClick={onClickSpontaneous}>
                    <WorkerEntryPointButton applicationType="spontaneous-apply" restaurantID={restaurant?.id}>
                      <Button
                        fullwidth
                        variant={scenario === 'B' ? 'primary' : 'secondary-neutral'}
                        StartIcon={PaperAirplaneIcon}
                      >
                        {t('job-position-page:offline_modal_spontaneous_cta')}
                      </Button>
                    </WorkerEntryPointButton>
                  </div>
                </div>
              ) : null}

              {/* Save-search card: always primary tinted. */}
              <div className="flex flex-col gap-4 p-5 md:p-6 rounded-2xl border bg-purple-50 border-purple-100">
                {/* Title icon: MagnifyingGlassPurpleIcon = MagnifyingGlassIcon with text-primary-800 */}
                <div className="flex items-center gap-2">
                  <MagnifyingGlassIcon className="h-5 w-5 text-primary-800 shrink-0" />
                  <Heading size={4} weight="semibold" color="default">
                    {t('job-position-page:offline_modal_save_search_title')}
                  </Heading>
                </div>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  <Trans
                    i18nKey="job-position-page:offline_modal_save_search_lead"
                    values={{ role: originRole }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <Button fullwidth variant="primary" StartIcon={BellAlertIcon} onClick={onClickSaveSearch}>
                  {t('job-position-page:offline_modal_save_search_cta')}
                </Button>
              </div>

              {/* External resource (salary hub) sits in the low-emphasis tail — after
                  both CTAs and before the explore fallback — so it doesn't compete with
                  the two primary actions. Scenario B only (no similar jobs to show). */}
              {scenario === 'B' ? (
                <ExternalResourceCard
                  title={t('job-position-page:offline_modal_resource_salary_title')}
                  href={SALARY_GUIDE_URL}
                  ctaLabel={t('job-position-page:offline_modal_resource_salary_cta')}
                />
              ) : null}

              {/* Explore fallback — always link to job explorer (restaurant page
                  is covered by the spontaneous card above). No "ghost"/"link"
                  Button variant in `ui-ds-base` (closest `tertiary` still has an
                  inset border) — using `next/link` styled as a plain text link to
                  de-emphasize this CTA. */}
              <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 text-center">{t('job-position-page:offline_modal_explore_lead')}</p>
                <Link
                  href="/cerco-lavoro"
                  className="inline-flex items-center justify-center gap-1.5 w-full text-sm font-semibold text-purple-700 no-underline hover:underline"
                  onClick={onClickExplore}
                >
                  {t('job-position-page:offline_modal_explore_jobs')}
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};
